// SPDX-FileCopyrightText: © 2023 Schneider Electric
//
// SPDX-License-Identifier: Apache-2.0

import type { ComponentInterface, EventEmitter } from "@stencil/core";
import {
  Component,
  Element,
  Event,
  Fragment,
  h,
  Host,
  Listen,
  Prop,
  State,
} from "@stencil/core";

import state from "../store";
import { onMobileBreakpoint } from "../utils";

/**
 * @slot side-nav - Side navigation content for the megamenu.
 * @slot side-links - Side links content for the megamenu.
 */

@Component({
  tag: "qds-web-megamenu",
  shadow: true,
  styleUrl: "megamenu.css",
})
export class Megamenu implements ComponentInterface {
  @Element() public el: HTMLElement;

  /**
   * Indicates whether the links slot is present in the megamenu.
   */
  @Prop() public readonly links: boolean = false;

  /**
   * The qds-standalone-link label.
   */
  @Prop() public readonly label: string;

  /**
   * Emitted when the megamenu is closed(all the nav items are closed).
   */
  @Event({ eventName: "qds-nav-item:close" })
  public closeNavTabs: EventEmitter<boolean>;

  @State() public megamneuContentHeight?: string;

  @State() public sideNavWidth?: number;

  @State() public isMobile: boolean;

  private cleanupMobileListener?: () => void;

  private megamenuEl?: HTMLElement;

  private mainMenuEl?: HTMLElement;

  @Listen("qds-nav-item:open", { target: "window" })
  public openNavItem(event: CustomEvent<boolean>): void {
    if (!this.isMobile) {
      document.body.style.overflow = event.detail ? "hidden" : "auto";
    }
  }

  public componentWillUpdate(): void {
    // reset scroll if megamenu content is open
    if (this.isMobile && this.megamenuEl !== undefined && state.item !== "") {
      this.megamenuEl.scrollTo(0, 0);
    }
  }

  public componentDidRender(): void {
    if (this.isMobile) {
      this.mainMenuEl?.focus();
    }
  }

  public componentWillLoad(): Promise<void> | void {
    this.cleanupMobileListener = onMobileBreakpoint((isMobile: boolean) => {
      this.isMobile = isMobile;
    }, this.el);
  }

  public disconnectedCallback(): void {
    this.cleanupMobileListener?.();
  }

  public render() {
    return (
      // eslint-disable-next-line jsx-a11y/aria-role
      <Host role="tabbar">
        <qds-web-list>
          <div
            class={{
              web: !this.isMobile,
              mobile: this.isMobile,
              "hide-overflow": this.isMobile && state.item !== "",
            }}
            ref={(element) => {
              this.megamenuEl = element as HTMLElement;
            }}
          >
            {this.isMobile && (
              <div class="menu-header">
                <qds-standalone-link
                  onClick={() => {
                    this.closeNavTabs.emit(true);
                  }}
                  onKeyDown={(event: KeyboardEvent) => {
                    if (event.key === "Enter") {
                      this.closeNavTabs.emit(true);
                    }
                  }}
                  importance="subdued"
                  size="large"
                  icon-name="chevron-left"
                  ref={(element: HTMLElement) => {
                    this.mainMenuEl = element;
                  }}
                >
                  {this.label}
                </qds-standalone-link>
                <qds-web-title-link text={state.tab} size="large" tag="h2" />
              </div>
            )}
            <div class="megamenu-wrapper">
              <div
                class={{
                  "side-nav flex": true,
                  "links-gap": this.links,
                  hide: this.isMobile && state.item !== "",
                }}
              >
                <div
                  class={{
                    "side-items": !this.isMobile,
                  }}
                  role="tablist"
                >
                  <slot name="side-nav" />
                </div>
                <div class="footer">
                  {this.links && (
                    <Fragment>
                      <qds-divider />
                      <div class="links" role="list">
                        <slot name="side-links" />
                      </div>
                    </Fragment>
                  )}
                </div>
              </div>
              <div class="content">
                <slot />
              </div>
              <div class="overflow-line" />
            </div>
          </div>
        </qds-web-list>
      </Host>
    );
  }
}
